import type { ThreadRowVariant } from "./ActiveThreadsSection.renderThreadRow";
import { mergeLiveAndReplicatedSidebarThreads } from "./mergeSidebarThreadHistory";
/**
 * ActiveThreadsSection
 *
 * Sidebar information architecture:
 *  1) `Projects` group — every Forge project that the user has registered,
 *     with its threads nested under it.
 *  2) `Loose threads` — defensive safety-net sub-row inside `Projects` for the
 *     rare case of a thread whose `projectId` doesn't resolve to a known
 *     project in the store (e.g. archived/missing project).
 */
import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useShallow } from "zustand/react/shallow";
import {
  type CollisionDetection,
  type DragCancelEvent,
  type DragEndEvent,
  type DragStartEvent,
  type useSensors as UseSensorsType,
} from "@dnd-kit/core";
import { scopedThreadKey, scopeThreadRef } from "@cmmd-forge/client-runtime";
import { useNavigate } from "@tanstack/react-router";
import {
  useStore,
  selectProjectsAcrossEnvironments,
  selectSidebarThreadsAcrossEnvironments,
} from "~/store";
import { usePrimaryEnvironmentDescriptor, usePrimaryEnvironmentId } from "~/environments/primary";
import { useUiStateStore, type SidebarDensity } from "~/uiStateStore";
import { useSettings } from "~/hooks/useSettings";
import { useProjectImportStore } from "~/projectImportStore";
import { useThreadSelectionStore } from "~/threadSelectionStore";
import { SidebarGroup } from "../ui/sidebar";
import { ThreadListShell } from "./ThreadListShell";
import type {
  SidebarProjectSortOrder,
  SidebarThreadSortOrder,
} from "@cmmd-forge/contracts/settings";
import type { SidebarProjectSnapshot } from "./SidebarProjectItem";
import { useActivityThreadRowRenderer } from "./ActiveThreadsSection.renderThreadRow";
import type { SidebarThreadSummary } from "~/types";
import type { useNewThreadHandler } from "~/hooks/useHandleNewThread";
import type { useThreadActions } from "~/hooks/useThreadActions";
import { useSidebarWorkerExpansion } from "./useSidebarWorkerExpansion";
import { useSidebarThreadHandlers } from "./useSidebarThreadHandlers";
import { WorkerSubRow } from "./WorkerSubRow";
import { projectSidebarThreads } from "./sidebarThreadProjection";
import {
  buildActiveThreadSectionModel,
  shouldShowLocalEnvironmentGroup,
} from "./activeThreadsSection.logic";
import { buildProjectDisplayByKey, lookupProjectDisplay } from "./projectDisplayIndex";
import { overlayCanonicalThreadDispositions } from "./canonicalThreadDispositionOverlay";
import { ActivitySidebarSection } from "./ActivitySidebarSection";
import { ACTIVITY_SECTION_ID } from "./activitySidebar.logic";
import { cmmdApiClient } from "~/environments/cmmdApi";
import {
  resolveCmmdProjectSettingsRoute,
  resolveProjectSettingsCmmdProjectId,
} from "./projectWorkspaceRoute";
import { toastManager } from "../ui/toast";
import { readRuntimeSurfaceState } from "../../environments/runtimeSurface";
import { useSavedEnvironmentRegistryStore } from "../../environments/runtime/catalog";
import { useSavedEnvironmentRuntimeStore } from "../../environments/runtime/savedEnvironmentRuntimeStore";
import { useCloudRuntimeProjectHandoffStore } from "./cloudRuntimeProjectHandoffStore";
import { useResolveEnvironmentLabel } from "./useResolveEnvironmentLabel";
import { useCmmdForgeRuntimeListSnapshot } from "../../cmmd/cmmdForgeRuntimeListResource";
import { useCmmdDeviceRegistrySnapshot } from "../../cmmd/cmmdDeviceRegistryResource";
import { buildCloudProjectIdentityByRuntimeId } from "./cloudProjectsGroup.logic";
import { cloudCatalogMembershipIsAuthoritative } from "./connectedCloudProjects.logic";
import { useSidebarCloudProjectBuckets } from "./useSidebarCloudProjectBuckets";
import { resolveCloudGroupState } from "./cloudRuntimeRowState.logic";
import { resolveVisibleProjectImportJobs } from "./cloudProjectImportJobs.logic";
import {
  collectRepresentedProjectIdentities,
  resolveVisibleCloudRows,
} from "../projects/visibleCloudRows";
import { useScopedPreConnectCloudRows } from "./cloudCatalogWorkspaceScope";
import { useCmmdRuntimeInventory } from "../../cmmd/useCmmdRuntimeInventory";
import { getStoredLocalDeviceKey } from "../../deviceRegistryIdentity";
import {
  buildRuntimeProjectGroups,
  resolveCanonicalProjectEmptyState,
  resolveCloudProjectStatus,
  UNKNOWN_PROJECT_DISPLAY,
} from "./buildRuntimeProjectGroups";
import { useCmmdWorkIntakeSnapshot } from "../../cmmd/workIntakeResource";
import { buildTaskWorkItemKeyIndex } from "../../lib/taskWorkItemKey";
import { TaskWorkItemKeyProvider } from "./TaskWorkItemKeyContext";
import { buildProjectGroups } from "./projectGroupedSidebar.logic";
import { useSidebarViewFiltersStore } from "~/sidebarViewFiltersStore";
import { ProjectGroupedSidebar } from "./ProjectGroupedSidebar";
import { selectArchivedCmmdProjectIds } from "./archivedCmmdProjectIds";
import { buildProjectRuntimeControls } from "./projectRuntimeControls.logic";
import { resolveDefaultDensity } from "./SidebarDensityToggle";
import {
  LEGACY_LOCAL_WORKSPACE_DENSITY_ID,
  PROJECTS_WORKSPACE_DENSITY_ID,
} from "./ProjectSidebarViewOptionsMenu";
import { useProjectDisplayNameResolver } from "../projects/useProjectDisplayNameResolver";
import { useProjectImportExpiryClock } from "./useProjectImportExpiryClock";
import { useSidebarDeviceDisconnect } from "./useSidebarDeviceDisconnect";
import {
  acknowledgeRelayControlRecovery,
  useRelayControlRecoveryByRuntimeId,
} from "../../environments/runtime/relayControlJournal";
// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ForgeProjectsProps {
  sortedProjects: readonly SidebarProjectSnapshot[];
  isManualProjectSorting: boolean;
  projectSortOrder: SidebarProjectSortOrder;
  threadSortOrder: SidebarThreadSortOrder;
  updateSettings: (
    patch: Partial<{
      sidebarProjectSortOrder: SidebarProjectSortOrder;
      sidebarThreadSortOrder: SidebarThreadSortOrder;
    }>,
  ) => void;
  shouldShowProjectPathEntry: boolean;
  handleStartAddProject: () => void;
  isElectron: boolean;
  expandedThreadListsByProject: ReadonlySet<string>;
  activeRouteProjectKey: string | null;
  routeThreadKey: string | null;
  newThreadShortcutLabel: string | null;
  handleNewThread: ReturnType<typeof useNewThreadHandler>["handleNewThread"];
  archiveThread: ReturnType<typeof useThreadActions>["archiveThread"];
  /** Returns an archived thread to the live list; only Activity's archived shelf offers it. */
  unarchiveThread: ReturnType<typeof useThreadActions>["unarchiveThread"];
  settleThread: ReturnType<typeof useThreadActions>["settleThread"];
  /** Same reach as `unarchiveThread`: only the Activity layout's Settled shelf offers it. */
  unsettleThread: ReturnType<typeof useThreadActions>["unsettleThread"];
  deleteThread: ReturnType<typeof useThreadActions>["deleteThread"];
  threadJumpLabelByKey: ReadonlyMap<string, string>;
  attachThreadListAutoAnimateRef: (el: HTMLElement | null) => void;
  expandThreadListForProject: (projectKey: string) => void;
  collapseThreadListForProject: (projectKey: string) => void;
  dragInProgressRef: React.MutableRefObject<boolean>;
  suppressProjectClickAfterDragRef: React.MutableRefObject<boolean>;
  suppressProjectClickForContextMenuRef: React.MutableRefObject<boolean>;
  attachProjectListAutoAnimateRef: (el: HTMLElement | null) => void;
  projectDnDSensors: ReturnType<typeof UseSensorsType>;
  projectCollisionDetection: CollisionDetection;
  handleProjectDragEnd: (event: DragEndEvent) => void;
  handleProjectDragStart: (event: DragStartEvent) => void;
  handleProjectDragCancel: (event: DragCancelEvent) => void;
}

export interface ActiveThreadsSectionProps extends ForgeProjectsProps {
  readonly navigationChrome: ReactNode;
  projectsLength: number;
  isBootstrappingLocal: boolean;
}

// ---------------------------------------------------------------------------
// ActiveThreadsSection
// ---------------------------------------------------------------------------

export const ActiveThreadsSection = memo(function ActiveThreadsSection(
  props: ActiveThreadsSectionProps,
) {
  const {
    navigationChrome,
    sortedProjects,
    isManualProjectSorting,
    projectSortOrder,
    threadSortOrder,
    updateSettings,
    handleStartAddProject,
    routeThreadKey,
    newThreadShortcutLabel,
    handleNewThread,
    archiveThread,
    unarchiveThread,
    settleThread,
    unsettleThread,
    deleteThread,
    threadJumpLabelByKey,
    attachThreadListAutoAnimateRef,
    expandThreadListForProject,
    collapseThreadListForProject,
    dragInProgressRef,
    suppressProjectClickAfterDragRef,
    suppressProjectClickForContextMenuRef,
    expandedThreadListsByProject,
    attachProjectListAutoAnimateRef,
    projectDnDSensors,
    projectCollisionDetection,
    handleProjectDragStart,
    handleProjectDragEnd,
    handleProjectDragCancel,
    isBootstrappingLocal,
    isElectron,
  } = props;
  const cloudSnapshot = useCmmdForgeRuntimeListSnapshot();
  const cloudState = resolveCloudGroupState(cloudSnapshot);
  const deviceRegistrySnapshot = useCmmdDeviceRegistrySnapshot();
  const relayRecoveryByRuntimeId = useRelayControlRecoveryByRuntimeId();
  const allProjectImportJobs = useProjectImportStore((state) => state.jobs);
  const dismissProjectImportJob = useProjectImportStore((state) => state.dismissJob);
  const { projects: cmmdProjects, tasks: cmmdTasks } = useCmmdWorkIntakeSnapshot();
  const archivedCmmdProjectIds = selectArchivedCmmdProjectIds(cmmdProjects);
  const taskWorkItemKeyIndex = useMemo(
    () => buildTaskWorkItemKeyIndex(cmmdProjects, cmmdTasks),
    [cmmdProjects, cmmdTasks],
  );
  const savedEnvironmentRegistry = useSavedEnvironmentRegistryStore((state) => state.byId);
  const savedEnvironmentRuntimeById = useSavedEnvironmentRuntimeStore((state) => state.byId);
  const cloudRuntimeProjectHandoffs = useCloudRuntimeProjectHandoffStore(
    (state) => state.byRuntimeId,
  );
  const reconcileCloudRuntimeProjectHandoffs = useCloudRuntimeProjectHandoffStore(
    (state) => state.reconcileCatalog,
  );
  const primaryEnvironmentDescriptor = usePrimaryEnvironmentDescriptor();
  const primaryEnvironmentId = usePrimaryEnvironmentId();

  const storeSnapshot = useStore(
    useShallow((state) => ({
      activeEnvironmentId: state.activeEnvironmentId,
      environmentStateById: state.environmentStateById,
    })),
  );
  const allProjects = useMemo(
    () => selectProjectsAcrossEnvironments(storeSnapshot),
    [storeSnapshot],
  );
  const sidebarThreads = useMemo(
    () => selectSidebarThreadsAcrossEnvironments(storeSnapshot),
    [storeSnapshot],
  );
  const runtimeInventory = useCmmdRuntimeInventory({
    registeredRuntimes: deviceRegistrySnapshot.registry?.runtimes ?? [],
    registeredDevices: deviceRegistrySnapshot.registry?.devices ?? [],
  });
  const deviceDisconnectController = useSidebarDeviceDisconnect();
  const currentRegisteredRuntimeId = runtimeInventory.currentRegisteredRuntimeId;
  const currentRegisteredDeviceKey = getStoredLocalDeviceKey();
  const authorizedSidebarThreads = overlayCanonicalThreadDispositions(
    sidebarThreads,
    deviceDisconnectController.disconnectedRuntimeIds,
    runtimeInventory.threadDispositions ?? [],
    savedEnvironmentRegistry,
  );
  const allSidebarThreads = mergeLiveAndReplicatedSidebarThreads(
    authorizedSidebarThreads,
    runtimeInventory.sidebarThreads,
    savedEnvironmentRegistry,
  );
  const visibleSidebarThreads = projectSidebarThreads(allSidebarThreads).visibleThreads;
  const sidebarLayoutMode = useSettings((s) => s.sidebarLayoutMode);
  const confirmThreadDelete = useSettings((s) => s.confirmThreadDelete);
  const appSettingsConfirmThreadArchive = useSettings((s) => s.confirmThreadArchive);
  const pinnedThreadById = useUiStateStore((s) => s.pinnedThreadById);
  const toggleThreadPinned = useUiStateStore((s) => s.toggleThreadPinned);
  const workspaceDensityById = useUiStateStore((s) => s.workspaceDensityById);
  const setWorkspaceDensity = useUiStateStore((s) => s.setWorkspaceDensity);

  const clearSelection = useThreadSelectionStore((state) => state.clearSelection);
  const toggleThreadSelection = useThreadSelectionStore((state) => state.toggleThread);
  const rangeSelectTo = useThreadSelectionStore((state) => state.rangeSelectTo);
  const removeFromSelection = useThreadSelectionStore((state) => state.removeFromSelection);
  const setSelectionAnchor = useThreadSelectionStore((state) => state.setAnchor);

  // Per-section row UI state (rename + archive confirmation)
  const [renamingThreadKey, setRenamingThreadKey] = useState<string | null>(null);
  const [renamingTitle, setRenamingTitle] = useState("");
  const [confirmingArchiveThreadKey, setConfirmingArchiveThreadKey] = useState<string | null>(null);
  const renamingCommittedRef = useRef(false);
  const renamingInputRef = useRef<HTMLInputElement | null>(null);
  const confirmArchiveButtonRefs = useRef(new Map<string, HTMLButtonElement>());

  /**
   * Every thread the sidebar RENDERS, keyed for the row handlers.
   *
   * Built from `allSidebarThreads`, not from `sidebarThreads`. The rows come
   * from the merged list — live threads plus the replicated ones from other
   * runtimes — and `handleThreadContextMenu` returns early when a row's key is
   * missing here. Keyed off the unmerged list, every replicated row rendered
   * normally and then did nothing at all on right-click: the handler ran, it
   * called `preventDefault`, and the menu never opened. The settled shelf is
   * where that showed up first, because the threads that reach it are old
   * enough to be replicated.
   *
   * The invariant is that this map covers exactly what is on screen, so it has
   * to be derived from the same list the rows are.
   */
  const sidebarThreadByKey = useMemo(
    () =>
      new Map(
        allSidebarThreads.map(
          (thread) =>
            [scopedThreadKey(scopeThreadRef(thread.environmentId, thread.id)), thread] as const,
        ),
      ),
    [allSidebarThreads],
  );
  const sidebarThreadByKeyRef =
    useRef<ReadonlyMap<string, SidebarThreadSummary>>(sidebarThreadByKey);
  sidebarThreadByKeyRef.current = sidebarThreadByKey;

  // runtimeId → CMMD project identity, from the catalog. Cloud rows are keyed by
  // PROJECT, so anything holding only a runtime id (a thread's environmentId, a
  // connected project's environment) has to come through here to find its row.
  const cloudProjectIdentityByRuntimeId = useMemo(
    () =>
      buildCloudProjectIdentityByRuntimeId(
        cloudSnapshot.rows,
        Object.values(cloudRuntimeProjectHandoffs),
      ),
    [cloudRuntimeProjectHandoffs, cloudSnapshot.rows],
  );
  const cloudCatalogIsAuthoritative = cloudCatalogMembershipIsAuthoritative(cloudSnapshot);
  useEffect(() => {
    if (!cloudCatalogIsAuthoritative) return;
    reconcileCloudRuntimeProjectHandoffs(cloudSnapshot.rows);
  }, [
    cloudCatalogIsAuthoritative,
    cloudRuntimeProjectHandoffs,
    cloudSnapshot.rows,
    reconcileCloudRuntimeProjectHandoffs,
  ]);

  const threadHandlers = useSidebarThreadHandlers({
    projectCwd: "",
    confirmThreadDelete,
    deleteThread,
    archiveThread,
    unarchiveThread,
    settleThread,
    unsettleThread,
    sidebarThreadByKeyRef,
    clearSelection,
    setSelectionAnchor,
    toggleThreadSelection,
    rangeSelectTo,
    removeFromSelection,
    setRenamingThreadKey,
    setRenamingTitle,
    renamingCommittedRef,
  });

  const projectsWithRuntimeInventory = useMemo(
    () => [...sortedProjects, ...runtimeInventory.sidebarProjects],
    [sortedProjects, runtimeInventory.sidebarProjects],
  );
  const {
    cloudConnectedProjects,
    cloudInventoryProjects,
    runtimeInventoryProjects,
    cloudInventoryRefsByRuntimeId,
    cloudConnectedProjectByRuntimeId,
  } = useSidebarCloudProjectBuckets({
    projects: projectsWithRuntimeInventory,
    projectIdentityByRuntimeId: cloudProjectIdentityByRuntimeId,
    savedEnvironments: savedEnvironmentRegistry,
    catalogRows: cloudSnapshot.rows,
  });
  // Hide the "This Device" (local environment) group on the cloud web surface —
  // there is no local machine there, so it is pointless chrome. The runtime
  // surface is boot-stable, so this is read once (mirrors useSidebarProjectsAndThreads).
  const showLocalEnvironment = useMemo(
    () => shouldShowLocalEnvironmentGroup(readRuntimeSurfaceState().surface),
    [],
  );
  const projectExpandedById = useUiStateStore((s) => s.projectExpandedById);
  const runtimeProjectGroups = useMemo(
    () =>
      buildRuntimeProjectGroups({
        projects: runtimeInventoryProjects,
        showLocalEnvironment,
        localEnvironmentLabel: primaryEnvironmentDescriptor?.label ?? null,
        savedEnvironmentRegistry,
        savedEnvironmentRuntimeById,
        registeredRuntimes: deviceRegistrySnapshot.registry?.runtimes ?? [],
        registeredDevices: deviceRegistrySnapshot.registry?.devices ?? [],
        registeredHosts: deviceRegistrySnapshot.registry?.hosts ?? [],
        currentRegisteredRuntimeId,
        currentRegisteredDeviceKey,
        runtimeInventory: runtimeInventory.runtimes,
        // Only PROVISIONED rows have a runtime environment id; an unprovisioned
        // project has no runtime environment to exclude.
        cloudRuntimeEnvironmentIds: new Set(
          cloudSnapshot.rows.flatMap((row) => (row.environmentId ? [row.environmentId] : [])),
        ),
      }),
    [
      runtimeInventoryProjects,
      primaryEnvironmentDescriptor?.label,
      savedEnvironmentRegistry,
      savedEnvironmentRuntimeById,
      deviceRegistrySnapshot.registry?.runtimes,
      deviceRegistrySnapshot.registry?.devices,
      deviceRegistrySnapshot.registry?.hosts,
      currentRegisteredRuntimeId,
      currentRegisteredDeviceKey,
      runtimeInventory.runtimes,
      cloudSnapshot.rows,
      showLocalEnvironment,
    ],
  );
  const runtimeGroupedProjects = useMemo(
    () => runtimeProjectGroups.flatMap((group) => group.projects),
    [runtimeProjectGroups],
  );
  const runtimeControls = useMemo(
    () =>
      buildProjectRuntimeControls({
        sources: runtimeProjectGroups,
        relayRecoveryByRuntimeId,
        disconnectingDeviceId: deviceDisconnectController.disconnectingDeviceId,
        disconnectDevice: deviceDisconnectController.disconnectDevice,
        acknowledgeRelayRecovery: acknowledgeRelayControlRecovery,
      }),
    [
      deviceDisconnectController.disconnectDevice,
      deviceDisconnectController.disconnectingDeviceId,
      relayRecoveryByRuntimeId,
      runtimeProjectGroups,
    ],
  );
  const projectImportExpiryNowMs = useProjectImportExpiryClock(allProjectImportJobs);
  const projectImportJobs = useMemo(
    () =>
      resolveVisibleProjectImportJobs({
        jobs: allProjectImportJobs,
        snapshot: cloudSnapshot,
        connectedCloudProjects: cloudConnectedProjects,
        catalogMembershipIsAuthoritative: cloudCatalogIsAuthoritative,
        nowMs: projectImportExpiryNowMs,
      }),
    [
      allProjectImportJobs,
      cloudCatalogIsAuthoritative,
      cloudConnectedProjects,
      cloudSnapshot,
      projectImportExpiryNowMs,
    ],
  );
  const visibleCloudImportJobs = useMemo(
    () => projectImportJobs.filter((job) => job.destination === "forge-cloud"),
    [projectImportJobs],
  );
  // Every Registration this device already knows about — the sidebar's own
  // connected cloud rows, plus every local/mixed project's worktree members —
  // so `resolveVisibleCloudRows` can exclude a catalog row by the Registration
  // it names rather than by which runtime it happens to share with a sibling.
  const cloudRepresentedProjectIdentities = useMemo(
    () =>
      collectRepresentedProjectIdentities([...cloudConnectedProjects, ...runtimeGroupedProjects]),
    [cloudConnectedProjects, runtimeGroupedProjects],
  );
  const visibleCloudRows = useMemo(
    () =>
      resolveVisibleCloudRows({
        catalogRows: cloudSnapshot.rows,
        catalogMembershipIsAuthoritative: cloudCatalogIsAuthoritative,
        archivedCmmdProjectIds,
        representedProjectIdentities: cloudRepresentedProjectIdentities,
        suppressingProjectImportJobs: visibleCloudImportJobs,
        surface: "sidebar",
        connectedProjectByRuntimeId: cloudConnectedProjectByRuntimeId,
        inventoryRefsByRuntimeId: cloudInventoryRefsByRuntimeId,
        inventoryProjects: cloudInventoryProjects,
      }),
    [
      archivedCmmdProjectIds,
      cloudCatalogIsAuthoritative,
      cloudConnectedProjectByRuntimeId,
      cloudInventoryProjects,
      cloudInventoryRefsByRuntimeId,
      cloudRepresentedProjectIdentities,
      cloudSnapshot.rows,
      visibleCloudImportJobs,
    ],
  );
  const visibleCloudProjects = useMemo(
    () => [...visibleCloudRows.connectedProjects, ...visibleCloudRows.orphanedInventoryProjects],
    [visibleCloudRows],
  );
  const preConnectCloudRows = useScopedPreConnectCloudRows(visibleCloudRows.preConnectCloudRows);
  const resolveEnvironmentLabel = useResolveEnvironmentLabel({
    savedEnvironmentRegistry,
    savedEnvironmentRuntimeById,
    deviceRegistry: deviceRegistrySnapshot.registry,
    currentRegisteredRuntimeId,
    currentRegisteredDeviceKey,
    runtimeInventoryRuntimes: runtimeInventory.runtimes,
    primaryEnvironmentId,
    primaryEnvironmentLabel: primaryEnvironmentDescriptor?.label ?? null,
  });

  // The Status control decides which threads are built into groups at all;
  // archived rows are dropped before this point unless it asks for them.
  const sidebarStatusFilter = useSidebarViewFiltersStore((state) => state.filters.status);
  const projectGroups = useMemo(
    () =>
      buildProjectGroups({
        mergedProjects: [...visibleCloudProjects, ...runtimeGroupedProjects],
        preConnectCloudRows,
        preConnectCloudMemberRefsByRuntimeId: cloudInventoryRefsByRuntimeId,
        allSidebarThreads,
        resolveEnvironmentLabel,
        statusFilter: sidebarStatusFilter,
        savedEnvironmentRegistry,
      }),
    [
      visibleCloudProjects,
      runtimeGroupedProjects,
      preConnectCloudRows,
      cloudInventoryRefsByRuntimeId,
      allSidebarThreads,
      resolveEnvironmentLabel,
      sidebarStatusFilter,
      savedEnvironmentRegistry,
    ],
  );
  const projectGroupOrder = useUiStateStore((state) => state.projectGroupOrder);
  const syncProjectGroups = useUiStateStore((state) => state.syncProjectGroups);
  const reorderProjectGroups = useUiStateStore((state) => state.reorderProjectGroups);
  useEffect(() => {
    syncProjectGroups(projectGroups.map((group) => group.groupKey));
  }, [projectGroups, syncProjectGroups]);
  const projectGroupOrderIndex = new Map(
    projectGroupOrder.map((groupKey, index) => [groupKey, index] as const),
  );
  const orderedProjectGroups = isManualProjectSorting
    ? projectGroups.toSorted(
        (left, right) =>
          (projectGroupOrderIndex.get(left.groupKey) ?? Number.MAX_SAFE_INTEGER) -
          (projectGroupOrderIndex.get(right.groupKey) ?? Number.MAX_SAFE_INTEGER),
      )
    : projectGroups;
  const projectsDensity =
    workspaceDensityById[PROJECTS_WORKSPACE_DENSITY_ID] ??
    workspaceDensityById[LEGACY_LOCAL_WORKSPACE_DENSITY_ID] ??
    resolveDefaultDensity(orderedProjectGroups.length);
  const handleProjectsDensityChange = (density: SidebarDensity) => {
    setWorkspaceDensity(PROJECTS_WORKSPACE_DENSITY_ID, density);
  };
  const handleProjectSortOrderChange = (order: SidebarProjectSortOrder) => {
    updateSettings({ sidebarProjectSortOrder: order });
  };
  const handleThreadSortOrderChange = (order: SidebarThreadSortOrder) => {
    updateSettings({ sidebarThreadSortOrder: order });
  };
  const handleCanonicalProjectDragEnd = (event: DragEndEvent) => {
    handleProjectDragEnd(event);
    if (!isManualProjectSorting || event.over === null || event.active.id === event.over.id) return;
    reorderProjectGroups(String(event.active.id), String(event.over.id));
  };

  // Worker expansion state (M4.1 + M4.6) — still used by project list below
  const { expandedWorkersByThread, toggleWorkersForThread } = useSidebarWorkerExpansion();

  const navigate = useNavigate();
  const handleNavigateToWorker = useCallback(
    (workerId: string, environmentId: string) => {
      const envId = environmentId || primaryEnvironmentId;
      if (!envId) return;
      void navigate({
        to: "/$environmentId/$threadId",
        params: {
          environmentId: envId as import("@cmmd-forge/contracts").EnvironmentId,
          threadId: workerId as import("@cmmd-forge/contracts").ThreadId,
        },
      });
    },
    [navigate, primaryEnvironmentId],
  );

  // One index for the favicon's path and the Activity card's project name, over
  // the SAME list that decides which threads are loose. Indexing only the live
  // store labelled every replica-backed row "Unknown project" whenever its
  // runtime was not connected, even though the replica carries the name.
  const projectsForDisplay = useMemo(
    () => [...allProjects, ...runtimeInventory.sidebarProjects],
    [allProjects, runtimeInventory.sidebarProjects],
  );
  // The groups are passed in as well as the snapshots: a group is the project
  // as the picker presents it, so resolving a card from the snapshots alone
  // gave the same project two identities in one sidebar.
  const projectDisplayByKey = useMemo(
    () => buildProjectDisplayByKey(projectsForDisplay, projectGroups, savedEnvironmentRegistry),
    [projectsForDisplay, projectGroups, savedEnvironmentRegistry],
  );
  const resolveProjectDisplayName = useProjectDisplayNameResolver();
  const resolveProjectDisplay = useCallback(
    (anchor: { readonly environmentId: string; readonly projectId: string }) => {
      const display =
        lookupProjectDisplay(projectDisplayByKey, anchor, savedEnvironmentRegistry) ??
        UNKNOWN_PROJECT_DISPLAY;
      // The index names a project after its checkout (a slug of the board title
      // for CMMD imports). The chosen name wins; every other field is left alone.
      const chosen = resolveProjectDisplayName(anchor.environmentId, anchor.projectId);
      return chosen === undefined ? display : { ...display, name: chosen };
    },
    [projectDisplayByKey, resolveProjectDisplayName, savedEnvironmentRegistry],
  );

  const sectionModel = useMemo(
    () =>
      buildActiveThreadSectionModel({
        allProjects: projectsForDisplay,
        visibleSidebarThreads,
        sidebarThreads: authorizedSidebarThreads,
        pinnedThreadById,
        threadSortOrder,
        sortedProjects: runtimeInventoryProjects,
        projectExpandedById,
      }),
    [
      projectsForDisplay,
      runtimeInventoryProjects,
      pinnedThreadById,
      projectExpandedById,
      authorizedSidebarThreads,
      threadSortOrder,
      visibleSidebarThreads,
    ],
  );

  const isActivityLayout = sidebarLayoutMode === "activity";
  const renderThreadRow = useActivityThreadRowRenderer({
    resolveProjectDisplay,
    resolveEnvironmentLabel,
    primaryEnvironmentId,
    savedEnvironmentRegistry,
    unarchiveThread,
    unsettleThread,
  });

  const renderWorkspaceThreadList = useCallback(
    (
      workspaceId: string,
      threads: readonly SidebarThreadSummary[],
      orderedThreadKeys: readonly string[] = threads.map((thread) =>
        scopedThreadKey(scopeThreadRef(thread.environmentId, thread.id)),
      ),
      resolveEnvironmentBadge?: (thread: SidebarThreadSummary) => React.ReactNode,
    ) => {
      // The one run of rows that renders slim. `workspaceId` is the section key
      // `ActivitySidebar` passes down, and both ends read it from
      // `ACTIVITY_SECTION_ID` — two bare string literals that had to stay equal
      // across two files is precisely the pair that drifts without failing:
      // the shelf would keep rendering, just as cards.
      const rowVariant: ThreadRowVariant = !isActivityLayout
        ? "project"
        : workspaceId === ACTIVITY_SECTION_ID.settled
          ? "archived"
          : "card";
      return (
        // Activity's list is flat, so it gets no gutter of its own: the cards sit
        // flush against the group's inset, the way t3code's do.
        <div
          key={`${workspaceId}:threads`}
          className={isActivityLayout ? "pb-0.5" : "px-1.5 pb-0.5"}
        >
          {threads.length > 0 ? (
            // `SidebarMenuSub` is the tree primitive — a left hairline plus an
            // indent, both of which mean "these rows belong to the row above
            // them". That is true in Projects and false in Activity, where the
            // cards ARE the list and the rail would just be a line down the
            // sidebar. t3code renders this list as a plain `ul`, so this does
            // too, rather than styling the rail away and leaving the primitive
            // to put it back the next time its base classes change.
            <ThreadListShell isFlat={isActivityLayout}>
              {threads.map((thread) => {
                const threadKey = scopedThreadKey(scopeThreadRef(thread.environmentId, thread.id));
                const projectCwd = resolveProjectDisplay(thread).cwd;
                const masterData = sectionModel.workersByMasterThreadKey.get(threadKey);
                const workersExpanded = expandedWorkersByThread.has(threadKey);
                return (
                  <Fragment key={threadKey}>
                    {renderThreadRow(
                      {
                        thread,
                        projectCwd,
                        orderedProjectThreadKeys: orderedThreadKeys,
                        isActive: routeThreadKey === threadKey,
                        jumpLabel: threadJumpLabelByKey.get(threadKey) ?? null,
                        appSettingsConfirmThreadArchive,
                        renamingThreadKey,
                        renamingTitle,
                        setRenamingTitle,
                        renamingInputRef,
                        renamingCommittedRef,
                        confirmingArchiveThreadKey,
                        setConfirmingArchiveThreadKey,
                        confirmArchiveButtonRefs,
                        handleThreadClick: threadHandlers.handleThreadClick,
                        navigateToThread: threadHandlers.navigateToThread,
                        handleMultiSelectContextMenu: threadHandlers.handleMultiSelectContextMenu,
                        handleThreadContextMenu: threadHandlers.handleThreadContextMenu,
                        clearSelection,
                        commitRename: threadHandlers.commitRename,
                        cancelRename: threadHandlers.cancelRename,
                        attemptArchiveThread: threadHandlers.attemptArchiveThread,
                        attemptSettleThread: threadHandlers.attemptSettleThread,
                        openPrLink: threadHandlers.openPrLink,
                        isPinned: pinnedThreadById[threadKey] === true,
                        onTogglePinned: toggleThreadPinned,
                        workerCount: masterData?.workers.length ?? 0,
                        attentionCount: masterData?.attention ?? 0,
                        workersExpanded,
                        onToggleWorkers: toggleWorkersForThread,
                        environmentBadge: resolveEnvironmentBadge?.(thread),
                        density: projectsDensity,
                      },
                      rowVariant,
                    )}
                    {/* An archived row is history: it does not fan out into its
                      workers, which are archived alongside it and have no live
                      state left to report. */}
                    {rowVariant !== "archived" &&
                      masterData &&
                      workersExpanded &&
                      masterData.workers.map((worker) => (
                        <WorkerSubRow
                          key={worker.id}
                          id={worker.id}
                          title={worker.title}
                          status={worker.status}
                          workerRole={worker.workerRole ?? null}
                          onNavigate={(workerId) =>
                            handleNavigateToWorker(workerId, masterData.environmentId)
                          }
                        />
                      ))}
                  </Fragment>
                );
              })}
            </ThreadListShell>
          ) : (
            <div className="px-2 py-1 text-[11px] text-muted-foreground/55">No threads yet</div>
          )}
        </div>
      );
    },
    [
      appSettingsConfirmThreadArchive,
      clearSelection,
      confirmArchiveButtonRefs,
      confirmingArchiveThreadKey,
      pinnedThreadById,
      resolveProjectDisplay,
      sectionModel.workersByMasterThreadKey,
      expandedWorkersByThread,
      toggleWorkersForThread,
      renamingCommittedRef,
      renamingInputRef,
      renamingThreadKey,
      renamingTitle,
      routeThreadKey,
      handleNavigateToWorker,
      threadHandlers.attemptArchiveThread,
      threadHandlers.attemptSettleThread,
      threadHandlers.cancelRename,
      threadHandlers.commitRename,
      threadHandlers.handleMultiSelectContextMenu,
      threadHandlers.handleThreadClick,
      threadHandlers.handleThreadContextMenu,
      threadHandlers.navigateToThread,
      threadHandlers.openPrLink,
      threadJumpLabelByKey,
      toggleThreadPinned,
      projectsDensity,
      renderThreadRow,
      isActivityLayout,
    ],
  );

  const setProjectExpanded = useUiStateStore((s) => s.setProjectExpanded);

  if (sidebarLayoutMode === "activity") {
    return (
      <TaskWorkItemKeyProvider value={taskWorkItemKeyIndex}>
        <ActivitySidebarSection
          navigationChrome={navigationChrome}
          projectGroups={orderedProjectGroups}
          archivedCmmdProjectIds={archivedCmmdProjectIds}
          visibleThreads={allSidebarThreads}
          pinnedThreadById={pinnedThreadById}
          threadSortOrder={threadSortOrder}
          onAddProject={handleStartAddProject}
          onProjectSettings={(project) => {
            // "Project settings" means the CMMD board's Settings tab — status,
            // priority, lead, target date, task key, repository. A project with
            // no board has nowhere to go: the execution workspace page that
            // used to serve as the fallback is gone, and it owned none of those
            // properties anyway. Say so rather than navigating to nothing.
            void resolveProjectSettingsCmmdProjectId({
              project,
              loadCmmdProjectLink: (params) => cmmdApiClient.getProjectLink(params),
            }).then((cmmdProjectId) => {
              if (cmmdProjectId === null) {
                toastManager.add({
                  type: "info",
                  title: "No project board linked",
                  description: "Link this project to a CMMD project to open its settings.",
                });
                return;
              }
              void navigate(resolveCmmdProjectSettingsRoute(cmmdProjectId));
            });
          }}
          renderThreadList={renderWorkspaceThreadList}
        />
        <deviceDisconnectController.DestructiveConfirmDialog />
        <threadHandlers.DestructiveConfirmDialog />
      </TaskWorkItemKeyProvider>
    );
  }

  return (
    <TaskWorkItemKeyProvider value={taskWorkItemKeyIndex}>
      {sectionModel.pinnedThreads.length > 0 ? (
        <SidebarGroup className="px-2 pt-1 pb-1">
          <div className="mb-1 flex items-center gap-2 px-2">
            <span className="text-[11px] font-medium tracking-[0.08em] text-muted-foreground/50 uppercase">
              Pinned
            </span>
            <span className="text-[10px] text-muted-foreground/40">
              {sectionModel.pinnedThreads.length}
            </span>
          </div>
          {renderWorkspaceThreadList(
            "__pinned__",
            sectionModel.pinnedThreads,
            sectionModel.pinnedThreadKeys,
          )}
        </SidebarGroup>
      ) : null}

      <ProjectGroupedSidebar
        navigationChrome={navigationChrome}
        groups={orderedProjectGroups}
        projectExpandedById={projectExpandedById}
        setProjectExpanded={setProjectExpanded}
        handleStartAddProject={handleStartAddProject}
        density={projectsDensity}
        projectSortOrder={projectSortOrder}
        threadSortOrder={threadSortOrder}
        onDensityChange={handleProjectsDensityChange}
        onProjectSortOrderChange={handleProjectSortOrderChange}
        onThreadSortOrderChange={handleThreadSortOrderChange}
        expandedThreadListsByProject={expandedThreadListsByProject}
        isManualProjectSorting={isManualProjectSorting}
        attachProjectListAutoAnimateRef={attachProjectListAutoAnimateRef}
        projectDnDSensors={projectDnDSensors}
        projectCollisionDetection={projectCollisionDetection}
        onProjectDragStart={handleProjectDragStart}
        onProjectDragEnd={handleCanonicalProjectDragEnd}
        onProjectDragCancel={handleProjectDragCancel}
        projectImportJobs={projectImportJobs}
        dismissProjectImportJob={dismissProjectImportJob}
        runtimeControls={runtimeControls}
        cloudStatus={resolveCloudProjectStatus(cloudState)}
        emptyState={resolveCanonicalProjectEmptyState({
          isBootstrappingLocal,
          isElectron,
        })}
        projectItemProps={{
          activeRouteThreadKey: routeThreadKey,
          newThreadShortcutLabel,
          handleNewThread,
          archiveThread,
          settleThread,
          deleteThread,
          threadJumpLabelByKey,
          attachThreadListAutoAnimateRef,
          expandThreadListForProject,
          collapseThreadListForProject,
          dragInProgressRef,
          suppressProjectClickAfterDragRef,
          suppressProjectClickForContextMenuRef,
          expandedWorkersByThread,
          onToggleWorkers: toggleWorkersForThread,
          onNavigateToWorker: handleNavigateToWorker,
          excludedThreadKeys: sectionModel.excludedFromProjectsThreadKeys,
          threadSource: allSidebarThreads,
          density: projectsDensity,
        }}
        looseThreads={sectionModel.looseThreads}
        renderLooseThreadList={renderWorkspaceThreadList}
      />
      <deviceDisconnectController.DestructiveConfirmDialog />
      <threadHandlers.DestructiveConfirmDialog />
    </TaskWorkItemKeyProvider>
  );
});
